import { Icon } from '@iconify/react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { UserStatus, type User, type UserTransaction } from '../../../types/ApiTypes';
import type { TransactionData } from '../../../types/TransactionDataType';
import BlockUserModal from '../../components/BlockUserModal';
import ConfirmPinModal from '../../components/ConfirmPinModal';
import Pagination from '../../components/Pagination';
import ReviewKYCModal from '../../components/ReviewKYCModal';
import SkeletonTable from '../../components/SkeletonTable';
import TransactionList from '../../components/TransactionList';
import { useApi } from '../../hooks/useApi';
import { usePagination } from '../../hooks/usePagination';
import Dashboard from "../../layout/Dashboard";
import { Content } from "../../styles/OverviewStyle";
import { Badge, MonoSmall, MonoUsername } from '../../styles/TableStyle';
import { formatDate } from '../../utils/dateHelpers';
import { formatWithCommas } from '@/utils/numberHelpers';

export default function UserDetails(): React.JSX.Element {
   const { userId } = useParams<{ userId: string }>()
   const navigate = useNavigate()
   const { user: userService } = useApi()
   const [userData, setUserData] = useState<User | null>(null)
   const [loadingUser, setLoadingUser] = useState(true)
   const [transactions, setTransactions] = useState<UserTransaction[]>([])
   const [loadingTxns, setLoadingTxns] = useState(true)
   const [totalPages, setTotalPages] = useState(1)
   const { pagination, setPage } = usePagination(1, 10)
   const [blockOpen, setBlockOpen] = useState(false)
   const [pinOpen, setPinOpen] = useState(false)
   const [kycOpen, setKycOpen] = useState(false)
   const [blockLoading, setBlockLoading] = useState(false)

   useEffect(() => {
      if (!userId) return
      const loadUser = async () => {
         setLoadingUser(true)
         const res = await userService.getUsers({ pageNumber: 1, pageSize: 1000 })
         if (res.status === 'success' && res.data) {
            const user = res.data.users.find(u => u.id === userId)
            if (user) {
               setUserData(user)
            }
         }
         setLoadingUser(false)
      }
      loadUser()
   }, [userId, userService])

   useEffect(() => {
      if (!userData) return
      const loadTransactions = async () => {
         setLoadingTxns(true)
         const res = await userService.getUserTransactions(userData.id, pagination)
         if (res.status === 'success' && res.data) {
            setTransactions(res.data.transactions)
            setTotalPages(res.data.totalPages || 1)
         }
         setLoadingTxns(false)
      }
      loadTransactions()
   }, [userData, userService, pagination])

   const formatTransactionType = (type: string): string => {
      return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
   }

   const mapTransactions = (txns: UserTransaction[]): TransactionData => {
      return txns.map(t => {
         const statusMap: Record<string, 'Completed' | 'Pending' | 'Failed'> = {
            completed: 'Completed',
            pending: 'Pending',
            failed: 'Failed'
         }
         return {
            ref: t.transactionId,
            user: `@${userData?.username}`,
            type: formatTransactionType(t.type),
            asset: t.asset ?? 'N/A',
            amount: `₦${formatWithCommas(t.amount)}`,
            rate: 'N/A',
            status: statusMap[t.status] || 'Pending',
            date: formatDate(t.date)
         }
      })
   }

   const handleSelectTransaction = (ref: string) => {
      navigate(`/transactions?txn=${ref}`)
   }

   if (loadingUser || !userData) return (
      <Dashboard title="User Management / User Details" icon="solar:user-linear" active="user_management">
         <Content>
            <div className='animate-pulse'>
               <div className='h-6 bg-gray-200 rounded w-48 mb-6'></div>
               <div className='border border-[#F0F0F2] rounded-md overflow-hidden'>
                  {[...Array(8)].map((_, i) => (
                     <div key={i} className='flex items-center gap-6 px-4 py-3 border-t first:border-t-0 border-[#F5F5F6]'>
                        <div className='w-[180px] h-4 bg-gray-200 rounded'></div>
                        <div className='flex-1 h-4 bg-gray-200 rounded'></div>
                     </div>
                  ))}
               </div>
            </div>
         </Content>
      </Dashboard>
   )


   return (
      <Dashboard title="User Management / User Details" icon="solar:user-linear" active="user_management">
         <Content>
            {/* Header with username and actions */}
            <div className='flex items-center justify-between'>
               <h1 className='text-[18px] font-semibold text-[#101018]'>{userData.username}</h1>
               <div className='flex items-center gap-3'>
                  {userData.status === UserStatus.VERIFIED && (
                     <button onClick={() => setBlockOpen(true)} disabled={blockLoading} className='px-3 py-2 cursor-pointer transition-all duration-200 rounded-full text-[12px] font-semibold tracking-tight text-[#DD524D] bg-white hover:bg-[#F9F9FA] flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed'>
                        <Icon icon='solar:forbidden-linear' width={16} />
                        {blockLoading ? 'Processing...' : 'Block User'}
                     </button>
                  )}
                  {userData.status === UserStatus.BLOCKED && (
                     <button onClick={() => setBlockOpen(true)} disabled={blockLoading} className='px-3 py-2 cursor-pointer transition-all duration-200 rounded-full text-[12px] font-semibold tracking-tight text-[#4CAF50] bg-white hover:bg-[#F9F9FA] flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed'>
                        <Icon icon='solar:check-circle-linear' width={16} />
                        {blockLoading ? 'Processing...' : 'Unblock User'}
                     </button>
                  )}
                  {userData.status === UserStatus.UNVERIFIED && (
                     <button onClick={() => setKycOpen(true)} className='px-3 py-2 cursor-pointer transition-all duration-200 rounded-full text-[12px] font-semibold tracking-tight text-white bg-[#AF7AD5] hover:bg-[#9b63c9]'>
                        Review KYC
                     </button>
                  )}
               </div>
            </div>

            {/* Summary */}
            <h2 className='mt-6 mb-2 text-[#101018] font-semibold'>Summary</h2>
            <div className='border border-[#F0F0F2] rounded-md overflow-hidden'>
               <SummaryRow label='Full name'>{userData.fullName}</SummaryRow>
               <SummaryRow label='Username'>
                  <MonoUsername>@{userData.username}</MonoUsername>
               </SummaryRow>
               <SummaryRow label='Phone number'>{userData.phone}</SummaryRow>
               <SummaryRow label='Registration date'>{formatDate(userData.registrationDate)}</SummaryRow>
               <SummaryRow label='Status'>
                  <Badge variant={userData.status === UserStatus.VERIFIED ? 'success' : userData.status === UserStatus.BLOCKED ? 'failed' : 'pending'} className='tracking-widest'>
                     {userData.status === UserStatus.VERIFIED ? 'Verified' : userData.status === UserStatus.BLOCKED ? 'Blocked' : 'Unverified'}
                  </Badge>
               </SummaryRow>
               <SummaryRow label='BVN'>
                  <MonoSmall>{userData.bvnNumber ?? 'N/A'}</MonoSmall>
               </SummaryRow>
               <SummaryRow label='NIN'>
                  <MonoSmall>{userData.ninNumber ?? 'N/A'}</MonoSmall>
               </SummaryRow>
               <SummaryRow label='Naira wallet'>
                  <span className='inline-flex items-center gap-2 text-[#101018] font-semibold'>
                     <Icon icon='circle-flags:ng' width={16} /> ₦{formatWithCommas(userData.wallet.ngn)}
                  </span>
               </SummaryRow>
               <SummaryRow label='Dollar wallet'>
                  <span className='inline-flex items-center gap-2 text-[#101018] font-semibold'>
                     <Icon icon='circle-flags:us' width={16} /> ${formatWithCommas(userData.wallet.usd)}
                  </span>
               </SummaryRow>
            </div>

            {/* Transaction history */}
            <h2 className='mt-10 mb-3 text-[#101018] font-semibold'>Transaction history</h2>

            {loadingTxns ? <SkeletonTable /> : (
               <>
                  <TransactionList data={mapTransactions(transactions)} onSelect={t => handleSelectTransaction(t.ref)} hideUsername hideRate />
                  {totalPages > 1 && (
                     <Pagination
                        currentPage={pagination.pageNumber ?? 1}
                        totalPages={totalPages}
                        onPageChange={setPage}
                     />
                  )}
               </>
            )}

            <BlockUserModal
               open={blockOpen}
               username={userData.username}
               isBlocked={userData.status === 'blocked'}
               onClose={() => setBlockOpen(false)}
               onConfirm={async () => {
                  setBlockOpen(false)
                  setBlockLoading(true)
                  try {
                     const res = await userService.blockUser(userData.id)
                     if (res.status === 'success') {
                        setUserData(prev => prev ? { ...prev, status: (prev.status === UserStatus.BLOCKED ? UserStatus.VERIFIED : UserStatus.BLOCKED) } : null)
                     }
                  } catch (error) {
                     toast.error(error instanceof Error ? error.message : 'An error occurred')
                  } finally {
                     setBlockLoading(false)
                  }
               }}
            />
            <ConfirmPinModal
               open={pinOpen}
               onClose={() => setPinOpen(false)}
               onConfirm={() => setPinOpen(false)}
            />
            <ReviewKYCModal
               open={kycOpen}
               username={userData.username}
               bvnNumber={userData.bvnNumber}
               onClose={() => setKycOpen(false)}
            />
         </Content>
      </Dashboard>
   )
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
   return (
      <div className='flex items-center gap-6 px-4 py-3 border-t first:border-t-0 border-[#F5F5F6]'>
         <div className='w-[180px] text-[12px] tracking-tight text-[#9C9C9F]'>{label}</div>
         <div className='flex-1 flex items-center gap-2 text-[14px] text-[#56565C]'>{children}</div>
      </div>
   )
}